import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/Kpi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { MessageCircle, Phone, MapPin, Trophy, ClipboardEdit } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [followOpen, setFollowOpen] = useState(false);
  const [follow, setFollow] = useState({ lead_id: "", mode: "call", disposition: "connected", remarks: "", next_followup_at: "" });

  const load = () => api.get(`/clients/${id}/timeline`).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const addFollow = async () => {
    try {
      await api.post("/followups", follow);
      toast.success("Follow-up logged");
      setFollowOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  if (!data) return <div className="text-muted-foreground">Loading client…</div>;
  const c = data.client;

  const iconFor = (kind) => {
    if (kind === "followup") return <Phone size={14}/>;
    if (kind === "visit") return <MapPin size={14}/>;
    if (kind === "booking") return <Trophy size={14}/>;
    return <ClipboardEdit size={14}/>;
  };

  return (
    <div className="space-y-6 animate-in">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Client 360°</div>
            <h1 className="font-serif-display text-3xl mt-1">{c.name}</h1>
            <div className="text-xs text-muted-foreground mt-1 font-mono">{c.client_code}</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Mobile</div><div className="font-mono">{c.mobile || "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Email</div><div>{c.email || "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">City</div><div>{c.city || "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Profession</div><div>{c.profession || "—"}</div></div>
            </div>
          </div>
          <Dialog open={followOpen} onOpenChange={setFollowOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-followup-btn"><MessageCircle size={14} className="mr-1"/> Log Follow-up</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log Follow-up</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Related Lead</Label>
                  <Select value={follow.lead_id} onValueChange={v => setFollow({ ...follow, lead_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select lead"/></SelectTrigger>
                    <SelectContent>
                      {data.leads.map(l => <SelectItem key={l.id} value={l.id}>{l.lead_code} — {l.stage}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Mode</Label>
                    <Select value={follow.mode} onValueChange={v => setFollow({ ...follow, mode: v })}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["call","whatsapp","email","meeting","site_visit","video","other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Disposition</Label>
                    <Select value={follow.disposition} onValueChange={v => setFollow({ ...follow, disposition: v })}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["connected","interested","hot","warm","cold","not_interested","call_back","no_answer","visit_planned"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Textarea value={follow.remarks} onChange={e => setFollow({ ...follow, remarks: e.target.value })} rows={3}/>
                </div>
                <div>
                  <Label>Next Follow-up</Label>
                  <Input type="datetime-local" value={follow.next_followup_at} onChange={e => setFollow({ ...follow, next_followup_at: e.target.value })}/>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addFollow} data-testid="followup-submit-btn">Save Follow-up</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-4">Timeline</div>
            <div className="relative pl-6 border-l border-border space-y-4">
              {data.timeline.map((e, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[29px] w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-accent">
                    {iconFor(e.kind)}
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}</div>
                  <div className="text-sm mt-0.5">
                    <span className="uppercase tracking-wider text-[10px] text-muted-foreground mr-2">{e.kind}</span>
                    {e.kind === "followup" && <>{e.data.mode} — <b>{e.data.disposition}</b>: {e.data.remarks}</>}
                    {e.kind === "activity" && <>{e.data.details}</>}
                    {e.kind === "visit" && <>Visit {e.data.visit_code} on {e.data.visit_date} — <b>{e.data.status}</b> {e.data.feedback ? `— ${e.data.feedback}` : ""}</>}
                    {e.kind === "booking" && <>Booking {e.data.booking_code} — {e.data.configuration} — <b>{e.data.booking_status}</b></>}
                  </div>
                </div>
              ))}
              {data.timeline.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
            </div>
          </Card>
        </div>
        <div className="space-y-4">
          <Card className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Project Interests</div>
            <div className="space-y-2">
              {data.leads.map(l => (
                <div key={l.id} className="border border-border rounded p-3">
                  <div className="flex justify-between items-center">
                    <div className="font-mono text-xs">{l.lead_code}</div>
                    <StatusBadge status={l.stage}/>
                  </div>
                  <div className="text-sm mt-1">{l.configuration || "-"} · {l.source}</div>
                </div>
              ))}
              {data.leads.length === 0 && <div className="text-sm text-muted-foreground">No leads linked.</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
